/**
 * Engagement admin — Iter 101.
 * Route: /app/engagement
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import RichTextEditor from "@/components/RichTextEditor";
import { toast, Toaster } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Sparkles, Plus, Trash2, Send, Eye, Heart } from "lucide-react";

const KINDS = ["quote", "news", "trend", "tip", "fact", "custom"];

export default function EngagementAdmin() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    if (!wsId) return;
    try {
      const [r, a] = await Promise.all([
        api.get(`/workspaces/${wsId}/engagement/items`),
        api.get(`/workspaces/${wsId}/engagement/analytics?days=30`),
      ]);
      setRows(r.data || []);
      setAnalytics(a.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { load(); }, [wsId]);

  const publish = async (id) => {
    try { await api.post(`/engagement/items/${id}/publish`); toast.success("Published"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Publish failed")); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/engagement/items/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="space-y-4" data-testid="engagement-admin-page">
      <Toaster richColors position="top-center" />
      <PageHeader title="Daily engagement widget"
                    subtitle="Quote / news / trend / tip of the day — configure what appears above the bulletin board."
                    icon={Sparkles}
                    actions={
                      <Button size="sm" className="rounded-none" onClick={() => setEditing({})}
                              data-testid="engagement-new-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" /> New item
                      </Button>
                    } />

      {/* Analytics tiles */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="engagement-analytics">
          <Tile label="Items published" value={analytics.published_items} />
          <Tile label="Total views" value={analytics.total_views} />
          <Tile label="Reactions" value={analytics.total_reactions} />
          <Tile label="Avg views / item" value={analytics.avg_views_per_item} />
          <Tile label="Audience size" value={analytics.audience_size} />
        </div>
      )}

      <AiGenerator wsId={wsId} onUseDraft={(draft) => setEditing(draft)} />
      <IdeaLibrary wsId={wsId} />

      {/* Items list */}
      <div className="border border-border divide-y divide-border" data-testid="engagement-items-list">
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">
            No engagement items yet. Add one to boost bulletin engagement.
          </div>
        ) : rows.map((it) => (
          <div key={it.id} className="p-3 flex items-start gap-3" data-testid={`engagement-row-${it.id}`}>
            <span className="text-[9px] uppercase font-bold tracking-widest px-2 py-0.5 bg-slate-100 text-slate-600">{it.kind}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{it.title || <em className="text-muted-foreground">Untitled</em>}</div>
              <div className="text-xs text-muted-foreground line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: (it.body_html || "").slice(0, 200) }} />
              <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                <span>{it.status}</span>
                {it.scheduled_for && <>· <span>{it.scheduled_for}</span></>}
                <span className="inline-flex items-center gap-0.5"><Eye className="w-3 h-3" /> {it.view_count || 0}</span>
                <span className="inline-flex items-center gap-0.5"><Heart className="w-3 h-3" /> {Object.values(it.reaction_summary || {}).reduce((a, b) => a + b, 0)}</span>
              </div>
            </div>
            {it.status !== "published" && (
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => publish(it.id)}
                       data-testid={`engagement-publish-${it.id}`}>
                <Send className="w-3 h-3 mr-1" /> Publish
              </Button>
            )}
            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setEditing(it)}
                     data-testid={`engagement-edit-${it.id}`}>Edit</Button>
            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs text-red-600" onClick={() => remove(it.id)}
                     data-testid={`engagement-delete-${it.id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>

      {editing && (
        <EngagementEditor wsId={wsId} item={editing.id ? editing : null}
                            initialDraft={editing.id ? null : editing}
                            onDone={() => { setEditing(null); load(); }}
                            onCancel={() => setEditing(null)} />
      )}
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="border border-border p-2 bg-muted/20">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function EngagementEditor({ wsId, item, initialDraft, onDone, onCancel }) {
  const isNew = !item;
  const [form, setForm] = useState(() => ({
    kind: item?.kind || initialDraft?.kind || "quote",
    title: item?.title || initialDraft?.title || "",
    body_html: item?.body_html || initialDraft?.body_html || "",
    author: item?.author || initialDraft?.author || "",
    source_url: item?.source_url || "",
    scheduled_for: item?.scheduled_for || new Date().toISOString().slice(0, 10),
    tags: (item?.tags || initialDraft?.tags || []).join(", "),
  }));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.body_html.trim()) { toast.error("Body required"); return; }
    setSaving(true);
    const payload = {
      ...form, tags: form.tags.split(/[,\s]+/).map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (isNew) await api.post(`/workspaces/${wsId}/engagement/items`, payload);
      else await api.patch(`/engagement/items/${item.id}`, payload);
      toast.success(isNew ? "Created" : "Saved");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="engagement-editor-modal">
      <div className="bg-background border border-border w-full max-w-2xl">
        <div className="px-4 py-3 border-b border-border font-semibold">
          {isNew ? "New engagement item" : `Edit: ${item.title || "item"}`}
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select className="h-9 border border-border px-2 text-sm bg-background"
                    value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    data-testid="engagement-editor-kind">
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <Input type="date" value={form.scheduled_for}
                    onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
                    data-testid="engagement-editor-scheduled" />
          </div>
          <Input placeholder="Title (optional)" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="engagement-editor-title" />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Body</label>
            <RichTextEditor value={form.body_html}
                             onChange={(html) => setForm({ ...form, body_html: html })}
                             placeholder="A quote, snippet, mini-story, or link with context. Rich text supported."
                             minHeight={140}
                             testid="engagement-editor-body" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Author (optional)" value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    data-testid="engagement-editor-author" />
            <Input placeholder="Source URL (optional)" value={form.source_url}
                    onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                    data-testid="engagement-editor-source" />
          </div>
          <Input placeholder="Tags (comma separated)" value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  data-testid="engagement-editor-tags" />
          <div className="flex justify-end gap-2 pt-3">
            <Button size="sm" variant="outline" className="rounded-none" onClick={onCancel}>Cancel</Button>
            <Button size="sm" className="rounded-none" onClick={save} disabled={saving}
                     data-testid="engagement-editor-save">
              {saving ? "Saving…" : (isNew ? "Create" : "Save")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────── AI content generator ───────────────────────

function AiGenerator({ wsId, onUseDraft }) {
  const [kind, setKind] = useState("quote");
  const [theme, setTheme] = useState("");
  const [count, setCount] = useState(3);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [meta, setMeta] = useState(null);

  const generate = async () => {
    setBusy(true); setDrafts([]);
    try {
      const r = await api.post(`/workspaces/${wsId}/engagement/generate`,
                                { kind, count, theme: theme || undefined });
      setDrafts(r.data.drafts || []);
      setMeta(r.data.context_sources);
      if (!(r.data.drafts || []).length) toast.warning("The AI didn't return any drafts. Add more ideas below and retry.");
    } catch (e) { toast.error(apiErrorMessage(e, "Generate failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-3 bg-gradient-to-br from-indigo-50/50 to-purple-50/50"
         data-testid="engagement-ai-generator">
      <div className="text-[11px] uppercase tracking-widest font-semibold mb-2 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-indigo-600" /> AI drafts &mdash; learns from top-reacted items + your ideas below
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <select className="h-9 border border-border px-2 text-sm bg-background" value={kind}
                onChange={(e) => setKind(e.target.value)} data-testid="engagement-gen-kind">
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <Input placeholder="Optional theme (e.g. resilience, teamwork, Q1 kickoff)"
                value={theme} onChange={(e) => setTheme(e.target.value)}
                className="flex-1 min-w-[200px]"
                data-testid="engagement-gen-theme" />
        <Input type="number" min={1} max={8} value={count}
                onChange={(e) => setCount(parseInt(e.target.value, 10) || 3)}
                className="w-20" data-testid="engagement-gen-count" />
        <Button size="sm" className="rounded-none" onClick={generate} disabled={busy}
                data-testid="engagement-gen-btn">
          {busy ? "Generating…" : "Generate drafts"}
        </Button>
      </div>
      {meta && (
        <div className="text-[10px] text-muted-foreground mt-1">
          Learning context: {meta.top_reacted} top-reacted items · {meta.admin_ideas} admin ideas
        </div>
      )}
      {drafts.length > 0 && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="engagement-gen-drafts">
          {drafts.map((d, i) => (
            <div key={i} className="border border-border bg-background p-2 text-xs" data-testid={`engagement-gen-draft-${i}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-[9px] uppercase font-bold bg-slate-100 px-1.5">{d.kind}</span>
                {d.title && <span className="font-semibold">{d.title}</span>}
              </div>
              <div className="text-muted-foreground line-clamp-3" dangerouslySetInnerHTML={{ __html: d.body_html }} />
              {d.author && <div className="text-[10px] italic mt-1">— {d.author}</div>}
              <div className="mt-2 flex justify-end">
                <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px]"
                         onClick={() => onUseDraft(d)}
                         data-testid={`engagement-gen-use-${i}`}>
                  Edit &amp; schedule →
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Ideas library ───────────────────────

function IdeaLibrary({ wsId }) {
  const [ideas, setIdeas] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/engagement/ideas`);
      setIdeas(r.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { if (wsId) load(); }, [wsId]);
  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/workspaces/${wsId}/engagement/ideas`, { text });
      setText(""); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/engagement/ideas/${id}`); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="border border-border p-3" data-testid="engagement-ideas-library">
      <div className="text-[11px] uppercase tracking-widest font-semibold mb-1">Ideas &amp; brand-voice inputs</div>
      <div className="text-[11px] text-muted-foreground mb-3">
        Authorized users can seed themes, brand voice notes, upcoming events, or anything the AI should learn from. Every idea is fed into future generations.
      </div>
      <div className="flex gap-2 mb-3">
        <Input placeholder="e.g. Prefer optimism & Indian cultural references · we launched product X this quarter · avoid clichés"
                value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && add()}
                data-testid="engagement-idea-input" />
        <Button size="sm" className="rounded-none" onClick={add} disabled={saving || !text.trim()}
                data-testid="engagement-idea-add">Add</Button>
      </div>
      <div className="border border-border divide-y divide-border max-h-56 overflow-y-auto" data-testid="engagement-ideas-list">
        {ideas.length === 0 ? (
          <div className="p-2 text-xs text-muted-foreground">No ideas yet — add a few to steer future AI drafts.</div>
        ) : ideas.map((it) => (
          <div key={it.id} className="p-2 text-xs flex items-start gap-2" data-testid={`engagement-idea-${it.id}`}>
            <div className="flex-1">{it.text}</div>
            <div className="text-[10px] text-muted-foreground">{it.created_by_name}</div>
            <button onClick={() => remove(it.id)} className="text-red-500 hover:text-red-700"
                     data-testid={`engagement-idea-del-${it.id}`}
                     aria-label="Delete idea"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

